// src/components/layout/Navbar.jsx
import React, { useState, useRef, useEffect } from 'react';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { getDisplayName, getRoleLabel } from '../../utils/userDisplay';

// Pastki bar — 4 ta asosiy + 1 avatar
const bottomItems = (t) => [
  { to: '/main',          label: t('nav.main'),          icon: 'home',                 end: true  },
  { to: '/navbat',        label: t('nav.queue'),          icon: 'format_list_numbered'             },
  { to: '/tolov',         label: t('nav.payment'),        icon: 'payments'                         },
  { to: '/notifications', label: t('nav.notifications'),  icon: 'notifications', hasNotif: true    },
];

// Desktop top nav — xuddi shu 4 ta
const desktopItems = (t) => [
  { to: '/main',          label: t('nav.main'),          icon: 'home',                end: true  },
  { to: '/navbat',        label: t('nav.queue'),          icon: 'format_list_numbered'            },
  { to: '/tolov',         label: t('nav.payment'),        icon: 'payments'                        },
  { to: '/notifications', label: t('nav.notifications'),  icon: 'notifications'                   },
];

// ─── Avatar dropdown (desktop top-bar) ───────────────────────────────────────
function AvatarDropdown({ user, initials, avatar, onLogout }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2.5 p-1.5 rounded-2xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
      >
        <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-[#1e9f6b] to-[#0d7a52] flex items-center justify-center text-white text-xs font-black overflow-hidden flex-shrink-0">
          {avatar ? <img src={avatar} alt="Avatar" className="h-full w-full object-cover" /> : initials}
        </div>
        <div className="hidden lg:block text-left">
          <p className="text-sm font-bold text-slate-900 dark:text-white leading-none truncate max-w-[130px]">{getDisplayName(user)}</p>
          {getRoleLabel(user) && <p className="text-xs text-slate-500 capitalize mt-0.5">{getRoleLabel(user)}</p>}
        </div>
        <span className={`material-symbols-outlined text-slate-400 text-[16px] hidden lg:block transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>expand_more</span>
      </button>

      <AnimatePresence>
        {open && (
          <>
          <motion.div
            role="presentation"
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-xl z-50 overflow-hidden"
          >
            {/* Foydalanuvchi info */}
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
              <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{getDisplayName(user)}</p>
              <p className="text-xs text-slate-500 mt-0.5 truncate">{user?.phone || user?.email || ''}</p>
            </div>

            <div className="p-1.5">
              <DropdownItem to="/profile"  icon="account_circle" label={t('profileModal.title')}  onClick={() => setOpen(false)} />
              <DropdownItem to="/orders"   icon="receipt_long"   label={t('nav.orders') || 'Buyurtmalar'} onClick={() => setOpen(false)} />
              <DropdownItem to="/tracking" icon="local_shipping" label={t('tracking.title') || 'Kuzatuv'} onClick={() => setOpen(false)} />
              <DropdownItem to="/support"  icon="support_agent"  label={t('support.title') || 'Yordam'} onClick={() => setOpen(false)} />
              <DropdownItem to="/settings" icon="settings"       label={t('settings.title')} onClick={() => setOpen(false)} />
              <DropdownItem to="/premium"  icon="workspace_premium" label={t("nav.premium")}               onClick={() => setOpen(false)} amber />
              {user?.role === 'admin' && (
                <DropdownItem to="/dashboard" icon="admin_panel_settings" label={t("nav.adminPanel")} onClick={() => setOpen(false)} green />
              )}
              <div className="h-px bg-slate-100 dark:bg-slate-800 my-1" />
              <button
                onClick={() => { setOpen(false); onLogout(); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <IconBox icon="logout" color="red" />
                {t('profileModal.logout')}
              </button>
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// Kichik yordamchi komponentlar
function IconBox({ icon, color = 'slate', filled = true }) {
  const colors = {
    slate:  'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400',
    amber:  'bg-amber-100 dark:bg-amber-900/30 text-amber-500',
    green:  'bg-[#1e9f6b]/10 text-[#1e9f6b]',
    red:    'bg-red-50 dark:bg-red-900/20 text-red-500',
  };
  return (
    <div className={`h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0 ${colors[color]}`}>
      <span className="material-symbols-outlined text-[15px]" style={{ fontVariationSettings: filled ? "'FILL' 1" : "'FILL' 0" }}>{icon}</span>
    </div>
  );
}

function DropdownItem({ to, icon, label, onClick, amber, green }) {
  const cls = amber
    ? 'text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20'
    : green
    ? 'text-[#1e9f6b] hover:bg-[#1e9f6b]/10'
    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800';
  const iconColor = amber ? 'amber' : green ? 'green' : 'slate';
  return (
    <Link to={to} onClick={onClick} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${cls}`}>
      <IconBox icon={icon} color={iconColor} />
      {label}
    </Link>
  );
}

// ─── Top Navbar ───────────────────────────────────────────────────────────────
export function Navbar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate('/login'); };
  const initials = user ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || 'U' : 'U';
  const avatar = user?.avatarUrl || user?.avatarDataUrl;

  return (
    <>
      <header className="sticky top-0 z-50 w-full h-[60px] bg-white/95 dark:bg-[var(--bg-surface)]/95 backdrop-blur-xl border-b border-[var(--border)]">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 h-full flex items-center justify-between gap-4">

          {/* Logo */}
          <Link to="/main" className="flex items-center gap-2.5 shrink-0">
            <div className="h-[34px] w-[34px] bg-gradient-to-br from-[#1e9f6b] to-[#0d7a52] rounded-[10px] flex items-center justify-center">
              <span className="material-symbols-outlined text-white text-[19px]" style={{ fontVariationSettings: "'FILL' 1" }}>local_gas_station</span>
            </div>
            <span className="sg-wordmark text-xl">Smart<em>Gaz</em></span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {desktopItems(t).map((item) => (
              <NavLink
                key={item.to} to={item.to} end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-bold transition-all duration-200 ${
                    isActive
                      ? 'bg-[#1e9f6b]/10 text-[#1e9f6b]'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`
                }
              >
                <span className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: "'FILL' 0" }}>{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Avatar dropdown */}
          <AvatarDropdown user={user} initials={initials} avatar={avatar} onLogout={handleLogout} />
        </div>
      </header>

      <BottomNavBar />
    </>
  );
}

// ─── Pastki navigatsiya bar ───────────────────────────────────────────────────
function BottomNavBar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initials = user ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || 'U' : 'U';
  const avatar = user?.avatarUrl || user?.avatarDataUrl;
  const items = bottomItems(t);

  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-50 bg-white/97 dark:bg-[var(--bg-surface)]/97 backdrop-blur-xl border-t border-[var(--border)] md:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="flex items-stretch h-[62px]">

        {items.map((item) => {
          const isActive = item.end
            ? location.pathname === item.to
            : location.pathname.startsWith(item.to);
          return (
            <NavLink
              key={item.to} to={item.to} end={item.end}
              className="flex-1 flex flex-col items-center justify-center gap-[3px] relative"
            >
              {/* Yuqori yashil chiziq */}
              {isActive && (
                <motion.div
                  layoutId="tab-bar-indicator"
                  className="absolute top-0 left-3 right-3 h-[2.5px] bg-[#1e9f6b] rounded-full"
                  transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                />
              )}

              {/* Icon pill */}
              <div className={`relative flex items-center justify-center w-[42px] h-[26px] rounded-[10px] transition-all duration-200 ${isActive ? 'bg-[#1e9f6b]/12' : ''}`}>
                <span
                  className={`material-symbols-outlined text-[22px] transition-all duration-200 ${isActive ? 'text-[#1e9f6b]' : 'text-slate-400 dark:text-slate-500'}`}
                  style={{ fontVariationSettings: isActive ? "'FILL' 1" : "'FILL' 0" }}
                >
                  {item.icon}
                </span>
                {/* Notification qizil nuqta */}
                {item.hasNotif && (
                  <span className="absolute top-0 right-[6px] h-[7px] w-[7px] bg-red-500 rounded-full border-[1.5px] border-white dark:border-[var(--bg-surface)]" />
                )}
              </div>

              {/* Label */}
              <span className={`text-[10px] font-bold leading-none transition-colors duration-200 ${isActive ? 'text-[#1e9f6b]' : 'text-slate-400 dark:text-slate-500'}`}>
                {item.label}
              </span>
            </NavLink>
          );
        })}

        {/* Avatar tab */}
        <MobileAvatarTab avatar={avatar} initials={initials} user={user} onLogout={() => { logout(); navigate('/login'); }} />
      </div>
    </nav>
  );
}

// ─── Mobil avatar tab ─────────────────────────────────────────────────────────
function MobileAvatarTab({ avatar, initials, user, onLogout }) {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const isActive = ['/profile', '/settings', '/premium', '/support', '/tracking', '/orders'].includes(location.pathname);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="flex-1 flex flex-col items-center justify-center gap-[3px] relative">
      {isActive && (
        <motion.div
          layoutId="tab-bar-indicator"
          className="absolute top-0 left-3 right-3 h-[2.5px] bg-[#1e9f6b] rounded-full"
          transition={{ type: 'spring', stiffness: 500, damping: 35 }}
        />
      )}

      <button onClick={() => setOpen(v => !v)} className="flex flex-col items-center gap-[3px]">
        <div className={`w-[26px] h-[26px] rounded-[8px] overflow-hidden flex items-center justify-center text-[10px] font-black text-white bg-gradient-to-br from-[#1e9f6b] to-[#0d7a52] transition-all ${isActive || open ? 'ring-2 ring-[#1e9f6b] ring-offset-1' : ''}`}>
          {avatar ? <img src={avatar} alt="Avatar" className="h-full w-full object-cover" /> : initials}
        </div>
        <span className={`text-[10px] font-bold leading-none ${isActive ? 'text-[#1e9f6b]' : 'text-slate-400 dark:text-slate-500'}`}>
          Profil
        </span>
      </button>

      {/* Yuqoriga chiquvchi menyu */}
      <AnimatePresence>
        {open && (
          <>
          <motion.div
            role="presentation"
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] cursor-default"
            onClick={() => setOpen(false)}
          />
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.96 }}
            transition={{ duration: 0.16 }}
            className="absolute bottom-[68px] right-2 w-52 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-2xl z-[70] overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
              <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{getDisplayName(user)}</p>
              <p className="text-xs text-slate-500 mt-0.5 truncate">{user?.phone || user?.email || ''}</p>
            </div>

            <div className="p-1.5">
              <DropdownItem to="/profile"  icon="account_circle"     label={t('profileModal.title')}  onClick={() => setOpen(false)} />
              <DropdownItem to="/orders"   icon="receipt_long"       label={t('nav.orders') || 'Buyurtmalar'} onClick={() => setOpen(false)} />
              <DropdownItem to="/tracking" icon="local_shipping"     label={t('tracking.title') || 'Kuzatuv'} onClick={() => setOpen(false)} />
              <DropdownItem to="/support"  icon="support_agent"      label={t('support.title') || 'Yordam'} onClick={() => setOpen(false)} />
              <DropdownItem to="/settings" icon="settings"           label={t('settings.title')} onClick={() => setOpen(false)} />
              <DropdownItem to="/premium"  icon="workspace_premium"  label={t("nav.premium")}                  onClick={() => setOpen(false)} amber />
              {user?.role === 'admin' && (
                <DropdownItem to="/dashboard" icon="admin_panel_settings" label={t("nav.adminPanel")} onClick={() => setOpen(false)} green />
              )}
              <div className="h-px bg-slate-100 dark:bg-slate-800 my-1" />
              <button
                onClick={() => { setOpen(false); onLogout(); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <IconBox icon="logout" color="red" />
                {t('profileModal.logout')}
              </button>
            </div>
          </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Page Layout ──────────────────────────────────────────────────────────────
export function PageLayout({ children }) {
  return (
    <div className="min-h-screen sg-user-app text-[var(--fg1)] transition-colors duration-300">
      <Navbar />
      <main className="max-w-[480px] md:max-w-[1280px] mx-auto px-5 md:px-8 py-7 pb-[88px] md:pb-8">
        {children}
      </main>
    </div>
  );
}
